/**
 * useUndoToast Hook
 *
 * Manages the instant-with-undo UX for block/unblock actions on the
 * availability calendar. (A-08, SCRUM-1143)
 *
 * Behaviour:
 *  - Caller provides a { message, onCommit, onUndo, onCommitError, onUndoError }
 *    descriptor. The hook schedules a 5-second commit window.
 *  - While the window is open, calling show() again commits the pending change
 *    first (single-toast-at-a-time rule) before starting a new window.
 *  - Calling undo() within the window calls onUndo and cancels the timer.
 *  - On unmount with an active window, onCommit fires (navigation-commits rule).
 *  - The hook returns { toastState, show, undo, dismiss } that the host
 *    component uses to render <UndoToast />.
 *
 * @param {number} [timeoutMs=5000] - Undo window duration in milliseconds
 * @returns {{ toastState: object|null, show: Function, undo: Function, dismiss: Function }}
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export default function useUndoToast({ timeoutMs = 5000 } = {}) {
  // toastState: null (no toast) or { message, onCommit, onUndo, onCommitError, onUndoError }
  const [toastState, setToastState] = useState(null);

  // Pending timer ref so we can cancel on undo or on new show()
  const timerRef = useRef(null);

  // Keep latest toastState accessible from cleanup without stale closure
  const toastStateRef = useRef(null);
  toastStateRef.current = toastState;

  /**
   * Cancel the running timer (if any).
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Commit the currently pending change immediately (without undo).
   * Called when: a second show() fires, or the timer elapses naturally.
   */
  const commitPending = useCallback(() => {
    const pending = toastStateRef.current;
    if (!pending) {
      return;
    }
    clearTimer();
    setToastState(null);

    // Fire-and-forget: run the API call, handle error via onCommitError
    Promise.resolve()
      .then(() => pending.onCommit())
      .catch(() => {
        if (pending.onCommitError) {
          pending.onCommitError();
        }
      });
  }, [clearTimer]);

  /**
   * Show a new undo toast.
   * If a toast is already active, commits it first (single-toast-at-a-time rule).
   *
   * @param {{ message: string, onCommit: Function, onUndo: Function, onCommitError?: Function, onUndoError?: Function }} descriptor
   */
  const show = useCallback((descriptor) => {
    // Single-toast rule: commit any pending change before showing new toast
    if (toastStateRef.current) {
      commitPending();
    }

    setToastState(descriptor);

    // Schedule auto-commit after timeoutMs
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = toastStateRef.current;
      if (!pending) {
        return;
      }
      setToastState(null);
      Promise.resolve()
        .then(() => pending.onCommit())
        .catch(() => {
          if (pending.onCommitError) {
            pending.onCommitError();
          }
        });
    }, timeoutMs);
  }, [commitPending, timeoutMs]);

  /**
   * User clicked Undo within the window.
   * Cancels the commit timer, calls onUndo, dismisses toast.
   */
  const undo = useCallback(() => {
    const pending = toastStateRef.current;
    if (!pending) {
      return;
    }
    clearTimer();
    setToastState(null);

    Promise.resolve()
      .then(() => pending.onUndo())
      .catch(() => {
        if (pending.onUndoError) {
          pending.onUndoError();
        }
      });
  }, [clearTimer]);

  /**
   * Dismiss without undo and without committing (e.g. on error toast removal).
   */
  const dismiss = useCallback(() => {
    clearTimer();
    setToastState(null);
  }, [clearTimer]);

  // Navigation-commits rule: if component unmounts with an active undo window,
  // fire onCommit so the change is persisted.
  useEffect(() => {
    return () => {
      clearTimer();
      const pending = toastStateRef.current;
      if (pending) {
        Promise.resolve()
          .then(() => pending.onCommit())
          .catch(() => {
            if (pending.onCommitError) {
              pending.onCommitError();
            }
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { toastState, show, undo, dismiss };
}
